import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Globe } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface GlobalStatus {
  id: string;
  component_name: string;
  status: string;
  description: string | null;
  updated_at: string;
  created_at: string;
}

export function GlobalSupabaseStatus() {
  const { t } = useTranslation(["superadmin", "common"]);
  const { data: globalStatuses, isLoading } = useQuery({
    queryKey: ['supabase-global-status'],
    queryFn: async () => {
      // Get latest status for each component
      const { data, error } = await supabase
        .from('supabase_global_status')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Get only the latest status for each component
      const latestStatuses: { [key: string]: GlobalStatus } = {};
      data?.forEach((status: GlobalStatus) => {
        if (!latestStatuses[status.component_name]) {
          latestStatuses[status.component_name] = status;
        }
      });

      return Object.values(latestStatuses);
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const getStatusColor = (status: string): "default" | "secondary" | "destructive" | "success" => {
    switch (status) {
      case 'operational':
        return 'success';
      case 'degraded_performance':
        return 'secondary';
      case 'partial_outage':
        return 'secondary';
      case 'major_outage':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'operational':
        return t("globalStatus.operational");
      case 'degraded_performance':
        return t("globalStatus.degraded");
      case 'partial_outage':
        return t("globalStatus.partialOutage");
      case 'major_outage':
        return t("globalStatus.majorOutage");
      default:
        return status;
    }
  };

  const getComponentLabel = (component: string): string => {
    return component
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const hasActiveIncident = globalStatuses?.some(
    s => s.status !== 'operational'
  );

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="h-5 w-5" />
          <h3 className="text-lg font-semibold">{t("globalStatus.title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("globalStatus.loading")}</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          <h3 className="text-lg font-semibold">{t("globalStatus.title")}</h3>
        </div>
        <a
          href="https://status.supabase.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("globalStatus.viewDetails")}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {globalStatuses && globalStatuses.length > 0 ? (
        <>
          {hasActiveIncident && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive font-medium">
                ⚠️ {t("globalStatus.incidentDetected")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("globalStatus.someServicesIssue")}{' '}
                <a
                  href="https://status.supabase.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  status.supabase.com
                </a>{' '}
                {t("globalStatus.moreInfo")}
              </p>
            </div>
          )}

          <div className="space-y-2">
            {globalStatuses.map((status) => (
              <div
                key={status.component_name}
                className="flex items-center justify-between py-2"
              >
                <span className="text-sm font-medium">
                  {getComponentLabel(status.component_name)}
                </span>
                <Badge variant={getStatusColor(status.status)}>
                  {getStatusLabel(status.status)}
                </Badge>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            {t("globalStatus.lastUpdate")}:{' '}
            {globalStatuses[0] &&
              new Date(globalStatuses[0].created_at).toLocaleString('fr-FR')}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("globalStatus.noData")}
        </p>
      )}
    </Card>
  );
}
